import React, { useCallback } from 'react';
import { Fuel } from 'lucide-react';
import type { SearchFilters, VehicleConfig, StationRecommendation } from '@/types';

interface SearchParametersProps {
  filters: SearchFilters;
  fuelAmount: number;
  vehicleConfig: VehicleConfig;
  recommendations: any[];
  averageFuelPrice: number;
  minFuelPrice: number;
  maxFuelPrice: number;
  priceGap: number;
  minPriceStation: StationRecommendation | null;
  maxPriceStation: StationRecommendation | null;
  recommendationsLoading: boolean;
  onFilterChange: (newFilters: Partial<SearchFilters>) => void;
  onFuelAmountChange: (amount: number) => void;
  onCostPerMileChange: (costPerMile: number) => void;
  onMinPriceClick: (station: StationRecommendation) => void;
  onMaxPriceClick: (station: StationRecommendation) => void;
}

const labelStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 4,
  fontSize: 11, fontWeight: 600, color: 'var(--paper-dim)',
  textTransform: 'uppercase', letterSpacing: '0.08em',
  marginBottom: 6,
};

const rangeEndStyle: React.CSSProperties = {
  fontSize: 10, color: 'var(--paper-dim)',
};

export const SearchParameters: React.FC<SearchParametersProps> = React.memo(({
  filters,
  fuelAmount,
  vehicleConfig,
  averageFuelPrice,
  onFilterChange,
  onFuelAmountChange,
  onCostPerMileChange,
}) => {
  const handleTripTypeChange = useCallback((tripType: 'single' | 'return') => {
    onFilterChange({ tripType });
  }, [onFilterChange]);

  const handleFuelTypeChange = useCallback((fuelType: string) => {
    onFilterChange({ fuelTypes: [fuelType] });
  }, [onFilterChange]);

  const handleRadiusChange = useCallback((radius: number) => {
    onFilterChange({ radius });
  }, [onFilterChange]);

  const handleSortChange = useCallback((sortBy: string) => {
    onFilterChange({ sortBy: sortBy as any });
  }, [onFilterChange]);

  // MPG conversion helpers
  const effectiveFuelPrice = averageFuelPrice > 0 ? averageFuelPrice : 145;
  const currentMpg = Math.min(100, Math.max(20,
    Math.round((effectiveFuelPrice * 4.546) / vehicleConfig.costPerMile)
  ));

  const handleMpgChange = useCallback((mpg: number) => {
    const cpm = Math.round((effectiveFuelPrice * 4.546) / mpg * 10) / 10;
    onCostPerMileChange(cpm);
  }, [effectiveFuelPrice, onCostPerMileChange]);

  // Slider fill percentages
  const fuelAmountPct = (((fuelAmount - 10) / (100 - 10)) * 100).toFixed(1);
  const mpgPct = (((currentMpg - 20) / (100 - 20)) * 100).toFixed(1);
  const radiusPct = (((filters.radius - 1) / (50 - 1)) * 100).toFixed(1);

  return (
    <div>
      <div style={{ overflow: 'hidden' }}>
        <div className="px-5 pt-4 pb-3">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-x-5 gap-y-4">

            {/* Trip Type */}
            <div>
              <p style={labelStyle}>Trip</p>
              <div style={{ display: 'flex', background: 'var(--paper-line)', borderRadius: 8, padding: 2, gap: 2 }}>
                <button
                  onClick={() => handleTripTypeChange('single')}
                  style={{
                    flex: 1, padding: '6px 8px', fontSize: 12.5, fontWeight: 500,
                    borderRadius: 6, transition: 'all 0.15s',
                    background: filters.tripType === 'single' ? 'var(--paper-2)' : 'transparent',
                    color: filters.tripType === 'single' ? 'var(--paper-fg)' : 'var(--paper-mute)',
                    boxShadow: filters.tripType === 'single' ? '0 1px 2px rgba(0,0,0,0.18)' : 'none',
                  }}
                >Single</button>
                <button
                  onClick={() => handleTripTypeChange('return')}
                  style={{
                    flex: 1, padding: '6px 8px', fontSize: 12.5, fontWeight: 500,
                    borderRadius: 6, transition: 'all 0.15s',
                    background: filters.tripType === 'return' ? 'var(--paper-2)' : 'transparent',
                    color: filters.tripType === 'return' ? 'var(--paper-fg)' : 'var(--paper-mute)',
                    boxShadow: filters.tripType === 'return' ? '0 1px 2px rgba(0,0,0,0.18)' : 'none',
                  }}
                >Return</button>
              </div>
            </div>

            {/* Fuel Type */}
            <div>
              <p style={labelStyle}>
                <Fuel style={{ width: 11, height: 11, color: 'var(--amber)' }} />
                Fuel Type
              </p>
              <div className="relative">
                <select
                  value={filters.fuelTypes[0] || ''}
                  onChange={(e) => handleFuelTypeChange(e.target.value)}
                  style={{
                    width: '100%', fontSize: 12.5, fontWeight: 500,
                    border: '1px solid var(--paper-line-strong)',
                    borderRadius: 8, padding: '6px 28px 6px 10px',
                    background: 'var(--paper-2)', color: 'var(--paper-fg)',
                    appearance: 'none', cursor: 'pointer', outline: 'none',
                  }}
                >
                  <option value="E10">Petrol (E10)</option>
                  <option value="B7">Diesel (B7)</option>
                  <option value="E5">Premium Petrol (E5)</option>
                  <option value="SDV">Premium Diesel</option>
                </select>
                <div className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2" style={{ color: 'var(--paper-mute)' }}>
                  <svg width="10" height="6" viewBox="0 0 10 6" fill="none">
                    <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </div>
            </div>

            {/* Fuel Amount */}
            <div>
              <p style={labelStyle}>
                Fill: <span style={{ color: 'var(--amber)', fontFamily: 'var(--font-mono)' }}>{fuelAmount}L</span>
              </p>
              <input
                type="range" min="10" max="100" step="5"
                value={fuelAmount}
                style={{ '--val': `${fuelAmountPct}%` } as React.CSSProperties}
                onChange={(e) => onFuelAmountChange(parseInt(e.target.value))}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 3 }}>
                <span style={rangeEndStyle}>10L</span><span style={rangeEndStyle}>100L</span>
              </div>
            </div>

            {/* Efficiency (MPG) */}
            <div>
              <p style={labelStyle}>
                Efficiency: <span style={{ color: 'var(--amber)', fontFamily: 'var(--font-mono)' }}>{currentMpg} mpg</span>
                <span style={{ color: 'var(--paper-dim)', fontFamily: 'var(--font-mono)', marginLeft: 4 }}>≈ {((effectiveFuelPrice * 4.546) / currentMpg).toFixed(1)}p/mi</span>
              </p>
              <input
                type="range" min="20" max="100" step="1"
                value={currentMpg}
                style={{ '--val': `${mpgPct}%` } as React.CSSProperties}
                onChange={(e) => handleMpgChange(parseInt(e.target.value))}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 3 }}>
                <span style={rangeEndStyle}>20</span><span style={rangeEndStyle}>100 mpg</span>
              </div>
            </div>

            {/* Search Radius */}
            <div>
              <p style={labelStyle}>
                Radius: <span style={{ color: 'var(--amber)', fontFamily: 'var(--font-mono)' }}>{filters.radius}mi</span>
              </p>
              <input
                type="range" min="1" max="50"
                value={filters.radius}
                style={{ '--val': `${radiusPct}%` } as React.CSSProperties}
                onChange={(e) => handleRadiusChange(parseInt(e.target.value))}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 3 }}>
                <span style={rangeEndStyle}>1mi</span><span style={rangeEndStyle}>50mi</span>
              </div>
            </div>

            {/* Sort By */}
            <div>
              <p style={labelStyle}>Sort By</p>
              <div className="relative">
                <select
                  value={filters.sortBy}
                  onChange={(e) => handleSortChange(e.target.value)}
                  style={{
                    width: '100%', fontSize: 12.5, fontWeight: 500,
                    border: '1px solid var(--paper-line-strong)',
                    borderRadius: 8, padding: '6px 28px 6px 10px',
                    background: 'var(--paper-2)', color: 'var(--paper-fg)',
                    appearance: 'none', cursor: 'pointer', outline: 'none',
                  }}
                >
                  <option value="total_cost">Total Cost</option>
                  <option value="price">Fuel Price</option>
                  <option value="distance">Distance</option>
                  <option value="savings">Savings</option>
                </select>
                <div className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2" style={{ color: 'var(--paper-mute)' }}>
                  <svg width="10" height="6" viewBox="0 0 10 6" fill="none">
                    <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  return (
    prevProps.filters.tripType === nextProps.filters.tripType &&
    prevProps.filters.fuelTypes[0] === nextProps.filters.fuelTypes[0] &&
    prevProps.filters.radius === nextProps.filters.radius &&
    prevProps.filters.sortBy === nextProps.filters.sortBy &&
    prevProps.fuelAmount === nextProps.fuelAmount &&
    prevProps.vehicleConfig.costPerMile === nextProps.vehicleConfig.costPerMile &&
    prevProps.averageFuelPrice === nextProps.averageFuelPrice &&
    prevProps.onFilterChange === nextProps.onFilterChange &&
    prevProps.onFuelAmountChange === nextProps.onFuelAmountChange &&
    prevProps.onCostPerMileChange === nextProps.onCostPerMileChange
  );
});

SearchParameters.displayName = 'SearchParameters';

export default SearchParameters;
